// src/auth/AuthProvider.tsx
"use client";

import { PlayerItem } from "prisma-layer";
import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { RouteNames } from "@/types/route-names";
import { InventoryPokemonsOutput, ItemDeckData } from "types/web-app";

export enum Intent {
  SELECTING_POKEMON_FOR_MAIN_DECK = "selecting_pokemon_for_main_deck",
  SELECTING_POKEMONS_TO_TRAIN = "selecting_pokemons_to_train",
}

interface SPAContextValue {
  selectedPokemons: InventoryPokemonsOutput["player_pokemons"];
  setSelectedPokemons: Dispatch<
    SetStateAction<InventoryPokemonsOutput["player_pokemons"]>
  >;
  selectedItems: PlayerItem[];
  setSelectedItems: Dispatch<SetStateAction<PlayerItem[]>>;
  selectedItemDeck: ItemDeckData | undefined;
  setSelectedItemDeck: Dispatch<SetStateAction<ItemDeckData | undefined>>;
  intent: Intent | undefined;
  setIntent: Dispatch<SetStateAction<Intent | undefined>>;
  currentRoute: RouteNames | undefined;
  setCurrentRoute: Dispatch<SetStateAction<RouteNames | undefined>>;
  previousRoute: RouteNames | undefined;
  setPreviousRoute: Dispatch<SetStateAction<RouteNames | undefined>>;
  viewedPlayerId: string | undefined;
  setViewedPlayerId: Dispatch<SetStateAction<string | undefined>>;
  viewedDexSetId: string | undefined;
  setViewedDexSetId: Dispatch<SetStateAction<string | undefined>>;
  registerBackAction: (action: () => void) => () => void;
  popBackAction: () => boolean;
  clearBackActions: () => void;
  suppressBackButton: boolean;
  setSuppressBackButton: Dispatch<SetStateAction<boolean>>;
}

const SPAContext = createContext<SPAContextValue | null>(null);

export const SPAProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  type BackActionEntry = { id: number; action: () => void };
  const backActionId = useRef(0);

  const [selectedPokemons, setSelectedPokemons] = useState<
    InventoryPokemonsOutput["player_pokemons"]
  >([]);
  const [selectedItems, setSelectedItems] = useState<PlayerItem[]>([]);
  const [selectedItemDeck, setSelectedItemDeck] = useState<
    ItemDeckData | undefined
  >(undefined);
  const [intent, setIntent] = useState<Intent | undefined>(undefined);
  const [currentRoute, setCurrentRoute] = useState<RouteNames | undefined>();
  const [previousRoute, setPreviousRoute] = useState<RouteNames | undefined>();
  const [viewedPlayerId, setViewedPlayerId] = useState<string | undefined>(
    undefined,
  );
  const [viewedDexSetId, setViewedDexSetId] = useState<string | undefined>(undefined);
  const [backActions, setBackActions] = useState<BackActionEntry[]>([]);
  const backActionsRef = useRef(backActions);
  backActionsRef.current = backActions;
  const [suppressBackButton, setSuppressBackButton] = useState(false);

  const registerBackAction = useCallback((action: () => void) => {
    const id = backActionId.current++;
    setBackActions((prev) => [...prev, { id, action }]);

    return () => {
      setBackActions((prev) => prev.filter((entry) => entry.id !== id));
    };
  }, []);

  const popBackAction = useCallback(() => {
    // Invoke the top handler without removing it. Views unregister on unmount
    // (or when their useBackAction deps change). Removing on every click breaks
    // in-view multi-step backs (e.g. region zoom → full map).
    const entry = backActionsRef.current[backActionsRef.current.length - 1];
    if (!entry) return false;
    entry.action();
    return true;
  }, []);

  const clearBackActions = useCallback(() => setBackActions([]), []);

  // Reset back stack when returning to home
  useEffect(() => {
    if (!currentRoute) {
      clearBackActions();
    }
  }, [clearBackActions, currentRoute]);

  const value = useMemo<SPAContextValue>(
    () => ({
      selectedPokemons,
      setSelectedPokemons,
      selectedItems,
      setSelectedItems,
      selectedItemDeck,
      setSelectedItemDeck,
      intent,
      setIntent,
      currentRoute,
      setCurrentRoute,
      previousRoute,
      setPreviousRoute,
      viewedPlayerId,
      setViewedPlayerId,
      viewedDexSetId,
      setViewedDexSetId,
      registerBackAction,
      popBackAction,
      clearBackActions,
      suppressBackButton,
      setSuppressBackButton,
    }),
    [
      selectedPokemons,
      setSelectedPokemons,
      selectedItems,
      setSelectedItems,
      selectedItemDeck,
      setSelectedItemDeck,
      intent,
      setIntent,
      currentRoute,
      setCurrentRoute,
      previousRoute,
      setPreviousRoute,
      viewedPlayerId,
      setViewedPlayerId,
      viewedDexSetId,
      setViewedDexSetId,
      registerBackAction,
      popBackAction,
      clearBackActions,
      suppressBackButton,
    ],
  );

  return <SPAContext.Provider value={value}>{children}</SPAContext.Provider>;
};

export function useSPAContext() {
  const ctx = useContext(SPAContext);
  if (!ctx) throw new Error("useSPAContext must be used within <SPAProvider>");

  return ctx;
}
